/**
 * Admin Ladder Management Page
 * Manage SSBU rankings, challenge windows, and disputes
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    collection,
    getDocs,
    doc,
    getDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    writeBatch,
    query,
    orderBy,
    where,
    Timestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import type { LadderEntry, LadderSettings, Challenge, PlayerRoster } from '../types';

const DEFAULT_SETTINGS: Omit<LadderSettings, 'updatedAt' | 'updatedBy'> = {
    challengeWindowOpen: false,
    isPreseason: true,
    allowOpenChallenges: true,
    defaultChallengeRange: 3,
    topTierThreshold: 5,
    topTierChallengeRange: 1,
    defaultFormat: 'Bo3',
    notifyOnChallenge: true,
    notifyOnResult: true,
};

export default function AdminLadderPage() {
    const { user } = useAuth();
    const [rankings, setRankings] = useState<LadderEntry[]>([]);
    const [challenges, setChallenges] = useState<Challenge[]>([]);
    const [availablePlayers, setAvailablePlayers] = useState<PlayerRoster[]>([]);
    const [settings, setSettings] = useState<LadderSettings>(DEFAULT_SETTINGS as LadderSettings);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState<'rankings' | 'challenges' | 'settings'>('rankings');
    const [draggedPlayer, setDraggedPlayer] = useState<string | null>(null);
    const [showAddPlayer, setShowAddPlayer] = useState(false);

    const loadData = useCallback(async () => {
        try {
            // Load rankings
            const ladderQuery = query(collection(db, 'ssbuLadder'), orderBy('rank', 'asc'));
            const ladderSnap = await getDocs(ladderQuery);
            const ladderData = ladderSnap.docs.map(d => ({ id: d.id, ...d.data() })) as LadderEntry[];
            setRankings(ladderData);

            // Load challenges
            const challengesQuery = query(
                collection(db, 'ssbuChallenges'),
                where('status', 'in', ['pending', 'assigned', 'scheduled', 'awaiting_confirm', 'disputed']),
                orderBy('status', 'asc')
            );
            const challengesSnap = await getDocs(challengesQuery);
            const challengesData = challengesSnap.docs.map(d => ({ id: d.id, ...d.data() })) as Challenge[];
            setChallenges(challengesData);

            // Load settings
            const settingsDoc = await getDoc(doc(db, 'settings', 'ssbuLadder'));
            if (settingsDoc.exists()) {
                setSettings(settingsDoc.data() as LadderSettings);
            }

            // Load available players (Smash players not on ladder - include inactive)
            const rosterSnap = await getDocs(collection(db, 'playerRoster'));
            const rosterData = rosterSnap.docs
                .map(d => ({ id: d.id, ...d.data() }) as PlayerRoster)
                .filter(p => p.games.smashSolo || p.games.smashCrews || p.games.smashJVCrews);

            const ladderIds = new Set(ladderData.map(l => l.playerRosterId));
            setAvailablePlayers(rosterData.filter(p => !ladderIds.has(p.id)));
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Drag and drop handlers for reordering
    const handleDragStart = (playerId: string) => {
        setDraggedPlayer(playerId);
    };

    const handleDragOver = (e: React.DragEvent, targetId: string) => {
        e.preventDefault();
        if (draggedPlayer && draggedPlayer !== targetId) {
            // Visual feedback
            e.currentTarget.classList.add('border-primary-500', 'border-2');
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.currentTarget.classList.remove('border-primary-500', 'border-2');
    };

    const handleDrop = async (e: React.DragEvent, targetId: string) => {
        e.preventDefault();
        e.currentTarget.classList.remove('border-primary-500', 'border-2');

        if (!draggedPlayer || draggedPlayer === targetId) return;

        const draggedIndex = rankings.findIndex(r => r.id === draggedPlayer);
        const targetIndex = rankings.findIndex(r => r.id === targetId);

        if (draggedIndex === -1 || targetIndex === -1) return;

        // Reorder locally
        const newRankings = [...rankings];
        const [removed] = newRankings.splice(draggedIndex, 1);
        newRankings.splice(targetIndex, 0, removed);

        // Update ranks
        const updatedRankings = newRankings.map((r, i) => ({
            ...r,
            previousRank: r.rank,
            rank: i + 1
        }));

        setRankings(updatedRankings);
        setDraggedPlayer(null);

        // Save to Firestore
        setSaving(true);
        try {
            const batch = writeBatch(db);
            updatedRankings.forEach(r => {
                batch.update(doc(db, 'ssbuLadder', r.id), {
                    rank: r.rank,
                    previousRank: r.previousRank,
                    updatedAt: Timestamp.now()
                });
            });
            await batch.commit();
        } catch (error) {
            console.error('Error saving ranks:', error);
            loadData(); // Reload on error
        } finally {
            setSaving(false);
        }
    };

    // Move player up in rankings
    const movePlayerUp = async (playerId: string) => {
        const playerIndex = rankings.findIndex(r => r.id === playerId);
        if (playerIndex <= 0) return; // Already at top

        setSaving(true);
        try {
            const batch = writeBatch(db);
            const currentPlayer = rankings[playerIndex];
            const playerAbove = rankings[playerIndex - 1];

            // Swap ranks
            batch.update(doc(db, 'ssbuLadder', currentPlayer.id), {
                rank: playerAbove.rank,
                previousRank: currentPlayer.rank,
                updatedAt: Timestamp.now()
            });
            batch.update(doc(db, 'ssbuLadder', playerAbove.id), {
                rank: currentPlayer.rank,
                previousRank: playerAbove.rank,
                updatedAt: Timestamp.now()
            });

            await batch.commit();
            await loadData();
        } catch (error) {
            console.error('Error moving player up:', error);
        } finally {
            setSaving(false);
        }
    };

    // Move player down in rankings
    const movePlayerDown = async (playerId: string) => {
        const playerIndex = rankings.findIndex(r => r.id === playerId);
        if (playerIndex === -1 || playerIndex >= rankings.length - 1) return; // Already at bottom

        setSaving(true);
        try {
            const batch = writeBatch(db);
            const currentPlayer = rankings[playerIndex];
            const playerBelow = rankings[playerIndex + 1];

            // Swap ranks
            batch.update(doc(db, 'ssbuLadder', currentPlayer.id), {
                rank: playerBelow.rank,
                previousRank: currentPlayer.rank,
                updatedAt: Timestamp.now()
            });
            batch.update(doc(db, 'ssbuLadder', playerBelow.id), {
                rank: currentPlayer.rank,
                previousRank: playerBelow.rank,
                updatedAt: Timestamp.now()
            });

            await batch.commit();
            await loadData();
        } catch (error) {
            console.error('Error moving player down:', error);
        } finally {
            setSaving(false);
        }
    };

    // Sync all SSBU players from roster to ladder
    const syncAllPlayers = async () => {
        if (availablePlayers.length === 0) {
            alert('All SSBU players are already on the ladder!');
            return;
        }
        if (!confirm(`Add ${availablePlayers.length} players to the ladder?`)) return;

        setSaving(true);
        try {
            const batch = writeBatch(db);
            let currentRank = rankings.length;

            for (const player of availablePlayers) {
                currentRank++;
                const newEntry = {
                    playerRosterId: player.id,
                    playerName: `${player.firstName} ${player.lastName}`,
                    gamerTag: player.gamerTag || null,
                    rank: currentRank,
                    stats: {
                        challengesIssued: 0,
                        challengesWon: 0,
                        defensesPlayed: 0,
                        defensesWon: 0,
                        currentStreak: 0,
                        bestRank: currentRank,
                        weeksAtRank: 0
                    },
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now()
                };
                batch.set(doc(db, 'ssbuLadder', player.id), newEntry);
            }

            await batch.commit();
            await loadData();
        } catch (error) {
            console.error('Error syncing players:', error);
        } finally {
            setSaving(false);
        }
    };

    // Sync names from roster to ladder (fixes denormalized data when roster is updated)
    const syncNamesFromRoster = async () => {
        if (rankings.length === 0) {
            alert('No players on the ladder to sync!');
            return;
        }

        setSaving(true);
        try {
            // Load all roster data
            const rosterSnap = await getDocs(collection(db, 'playerRoster'));
            const rosterMap = new Map<string, PlayerRoster>();
            rosterSnap.docs.forEach(d => {
                rosterMap.set(d.id, { id: d.id, ...d.data() } as PlayerRoster);
            });

            // Find mismatches and update
            const batch = writeBatch(db);
            let updateCount = 0;

            for (const ladderEntry of rankings) {
                const rosterEntry = rosterMap.get(ladderEntry.playerRosterId);
                if (rosterEntry) {
                    const rosterName = `${rosterEntry.firstName} ${rosterEntry.lastName}`;
                    const rosterGamerTag = rosterEntry.gamerTag || null;

                    // Check if name or gamerTag differs
                    if (ladderEntry.playerName !== rosterName || ladderEntry.gamerTag !== rosterGamerTag) {
                        batch.update(doc(db, 'ssbuLadder', ladderEntry.id), {
                            playerName: rosterName,
                            gamerTag: rosterGamerTag,
                            updatedAt: Timestamp.now()
                        });
                        updateCount++;
                    }
                }
            }

            if (updateCount > 0) {
                await batch.commit();
                await loadData();
                alert(`Updated ${updateCount} player name(s) from roster!`);
            } else {
                alert('All names are already in sync with the roster!');
            }
        } catch (error) {
            console.error('Error syncing names:', error);
            alert('Error syncing names. Check console for details.');
        } finally {
            setSaving(false);
        }
    };

    // Add player to ladder
    const addPlayerToLadder = async (player: PlayerRoster) => {
        setSaving(true);
        try {
            const newRank = rankings.length + 1;
            const newEntry: Omit<LadderEntry, 'id'> = {
                playerRosterId: player.id,
                playerName: `${player.firstName} ${player.lastName}`,
                gamerTag: player.gamerTag,
                rank: newRank,
                stats: {
                    challengesIssued: 0,
                    challengesWon: 0,
                    defensesPlayed: 0,
                    defensesWon: 0,
                    currentStreak: 0,
                    bestRank: newRank,
                    weeksAtRank: 0
                }
            };

            await setDoc(doc(db, 'ssbuLadder', player.id), {
                ...newEntry,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });

            await loadData();
            setShowAddPlayer(false);
        } catch (error) {
            console.error('Error adding player:', error);
        } finally {
            setSaving(false);
        }
    };

    // Remove player from ladder
    const removePlayerFromLadder = async (playerId: string) => {
        if (!confirm('Remove this player from the ladder? Their stats will be lost.')) return;

        setSaving(true);
        try {
            await deleteDoc(doc(db, 'ssbuLadder', playerId));

            // Reorder remaining players
            const remaining = rankings.filter(r => r.id !== playerId);
            const batch = writeBatch(db);
            remaining.forEach((r, i) => {
                batch.update(doc(db, 'ssbuLadder', r.id), {
                    rank: i + 1,
                    previousRank: r.rank,
                    updatedAt: Timestamp.now()
                });
            });
            await batch.commit();

            await loadData();
        } catch (error) {
            console.error('Error removing player:', error);
        } finally {
            setSaving(false);
        }
    };

    // Toggle challenge window
    const toggleChallengeWindow = async () => {
        setSaving(true);
        try {
            const newOpen = !settings.challengeWindowOpen;
            const updates: Partial<LadderSettings> = {
                challengeWindowOpen: newOpen,
                updatedAt: Timestamp.now(),
                updatedBy: user?.uid
            };

            if (newOpen) {
                updates.currentWindowStart = Timestamp.now();
                // Set default end to 5 days from now
                const endDate = new Date();
                endDate.setDate(endDate.getDate() + 5);
                updates.currentWindowEnd = Timestamp.fromDate(endDate);
            }

            await setDoc(doc(db, 'settings', 'ssbuLadder'), {
                ...settings,
                ...updates
            }, { merge: true });

            setSettings(prev => ({ ...prev, ...updates }));
        } catch (error) {
            console.error('Error toggling window:', error);
        } finally {
            setSaving(false);
        }
    };

    // Save settings
    const saveSettings = async () => {
        setSaving(true);
        try {
            await setDoc(doc(db, 'settings', 'ssbuLadder'), {
                ...settings,
                updatedAt: Timestamp.now(),
                updatedBy: user?.uid
            });
        } catch (error) {
            console.error('Error saving settings:', error);
        } finally {
            setSaving(false);
        }
    };

    // Resolve challenge
    const resolveChallenge = async (challengeId: string, winner: 'challenger' | 'defender') => {
        setSaving(true);
        try {
            const challenge = challenges.find(c => c.id === challengeId);
            if (!challenge) return;

            // Update challenge
            await updateDoc(doc(db, 'ssbuChallenges', challengeId), {
                status: 'completed',
                result: { winner },
                completedDate: Timestamp.now(),
                resolvedBy: user?.uid,
                updatedAt: Timestamp.now()
            });

            // If challenger won, swap ranks
            if (winner === 'challenger') {
                const challengerEntry = rankings.find(r => r.playerRosterId === challenge.challengerId);
                const defenderEntry = rankings.find(r => r.playerRosterId === challenge.defenderId);

                if (challengerEntry && defenderEntry) {
                    const batch = writeBatch(db);

                    // Update challenger
                    batch.update(doc(db, 'ssbuLadder', challengerEntry.id), {
                        rank: defenderEntry.rank,
                        previousRank: challengerEntry.rank,
                        'stats.challengesWon': challengerEntry.stats.challengesWon + 1,
                        'stats.currentStreak': challengerEntry.stats.currentStreak > 0
                            ? challengerEntry.stats.currentStreak + 1
                            : 1,
                        'stats.bestRank': Math.min(challengerEntry.stats.bestRank, defenderEntry.rank),
                        updatedAt: Timestamp.now()
                    });

                    // Shift down all players between the two ranks
                    const playersToShift = rankings.filter(
                        r => r.rank >= defenderEntry.rank && r.rank < challengerEntry.rank && r.id !== challengerEntry.id
                    );

                    playersToShift.forEach(p => {
                        batch.update(doc(db, 'ssbuLadder', p.id), {
                            rank: p.rank + 1,
                            previousRank: p.rank,
                            updatedAt: Timestamp.now()
                        });
                    });

                    // Update defender stats
                    batch.update(doc(db, 'ssbuLadder', defenderEntry.id), {
                        'stats.defensesPlayed': defenderEntry.stats.defensesPlayed + 1,
                        'stats.currentStreak': defenderEntry.stats.currentStreak < 0
                            ? defenderEntry.stats.currentStreak - 1
                            : -1,
                        updatedAt: Timestamp.now()
                    });

                    await batch.commit();
                }
            } else {
                // Defender won - just update stats
                const challengerEntry = rankings.find(r => r.playerRosterId === challenge.challengerId);
                const defenderEntry = rankings.find(r => r.playerRosterId === challenge.defenderId);

                if (challengerEntry && defenderEntry) {
                    const batch = writeBatch(db);

                    batch.update(doc(db, 'ssbuLadder', challengerEntry.id), {
                        'stats.currentStreak': challengerEntry.stats.currentStreak < 0
                            ? challengerEntry.stats.currentStreak - 1
                            : -1,
                        updatedAt: Timestamp.now()
                    });

                    batch.update(doc(db, 'ssbuLadder', defenderEntry.id), {
                        'stats.defensesPlayed': defenderEntry.stats.defensesPlayed + 1,
                        'stats.defensesWon': defenderEntry.stats.defensesWon + 1,
                        'stats.currentStreak': defenderEntry.stats.currentStreak > 0
                            ? defenderEntry.stats.currentStreak + 1
                            : 1,
                        updatedAt: Timestamp.now()
                    });

                    await batch.commit();
                }
            }

            await loadData();
        } catch (error) {
            console.error('Error resolving challenge:', error);
        } finally {
            setSaving(false);
        }
    };

    // Cancel challenge
    const cancelChallenge = async (challengeId: string) => {
        if (!confirm('Cancel this challenge?')) return;

        setSaving(true);
        try {
            await updateDoc(doc(db, 'ssbuChallenges', challengeId), {
                status: 'cancelled',
                adminNotes: 'Cancelled by admin',
                resolvedBy: user?.uid,
                updatedAt: Timestamp.now()
            });
            await loadData();
        } catch (error) {
            console.error('Error cancelling challenge:', error);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-display font-bold text-white flex items-center gap-2">
                        ⚔️ SSBU Ladder Management
                    </h1>
                    <p className="text-secondary-400 text-sm mt-1">
                        Manage rankings, challenges, and settings
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <Link to="/ladder" className="btn-secondary text-sm" target="_blank">
                        View Public Page ↗
                    </Link>
                    <button
                        onClick={toggleChallengeWindow}
                        disabled={saving}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${settings.challengeWindowOpen
                            ? 'bg-red-500/20 text-red-400 border border-red-500/50 hover:bg-red-500/30'
                            : 'bg-green-500/20 text-green-400 border border-green-500/50 hover:bg-green-500/30'
                            }`}
                    >
                        {settings.challengeWindowOpen ? '🔒 Close Window' : '🔓 Open Window'}
                    </button>
                </div>
            </div>

            {/* Status Banner */}
            <div className={`p-4 rounded-lg flex items-center justify-between ${settings.challengeWindowOpen
                ? 'bg-green-500/10 border border-green-500/30'
                : 'bg-secondary-800 border border-secondary-700'
                }`}>
                <div className="flex items-center gap-3">
                    <span className={`w-3 h-3 rounded-full ${settings.challengeWindowOpen ? 'bg-green-400 animate-pulse' : 'bg-secondary-500'
                        }`}></span>
                    <span className="font-medium text-white">
                        Challenge Window: {settings.challengeWindowOpen ? 'OPEN' : 'CLOSED'}
                    </span>
                    {settings.isPreseason && (
                        <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded">
                            PRESEASON MODE
                        </span>
                    )}
                </div>
                <span className="text-secondary-400 text-sm">
                    {rankings.length} players • {challenges.length} active challenges
                </span>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-secondary-700">
                {['rankings', 'challenges', 'settings'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab as typeof activeTab)}
                        className={`px-4 py-2 font-medium capitalize transition-colors border-b-2 -mb-px ${activeTab === tab
                            ? 'text-red-400 border-red-500'
                            : 'text-secondary-400 border-transparent hover:text-white'
                            }`}
                    >
                        {tab}
                        {tab === 'challenges' && challenges.length > 0 && (
                            <span className="ml-2 px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
                                {challenges.length}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Rankings Tab */}
            {activeTab === 'rankings' && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <p className="text-secondary-400 text-sm">Drag to reorder or use arrows</p>
                        <div className="flex gap-2">
                            <button
                                onClick={syncNamesFromRoster}
                                disabled={saving || rankings.length === 0}
                                className="btn-secondary text-sm"
                                title="Update names &amp; gamertags from roster database"
                            >
                                ✏️ Sync Names
                            </button>
                            <button
                                onClick={syncAllPlayers}
                                disabled={saving || availablePlayers.length === 0}
                                className="btn-secondary text-sm"
                                title={availablePlayers.length === 0 ? 'All SSBU players are on the ladder' : `Add ${availablePlayers.length} players`}
                            >
                                🔄 Sync All ({availablePlayers.length})
                            </button>
                            <button
                                onClick={() => setShowAddPlayer(true)}
                                className="btn-primary text-sm"
                            >
                                + Add Player
                            </button>
                        </div>
                    </div>

                    <div className="space-y-2">
                        {rankings.map((player) => (
                            <div
                                key={player.id}
                                draggable
                                onDragStart={() => handleDragStart(player.id)}
                                onDragOver={(e) => handleDragOver(e, player.id)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, player.id)}
                                className={`card p-3 flex items-center gap-4 cursor-move hover:bg-secondary-700/50 transition-all ${draggedPlayer === player.id ? 'opacity-50' : ''
                                    }`}
                            >
                                <div className="text-secondary-500 cursor-grab">≡</div>
                                <div className="w-10 text-center font-bold text-white">
                                    #{player.rank}
                                </div>
                                <div className="flex-1">
                                    <span className="font-medium text-white">
                                        {player.gamerTag || player.playerName}
                                    </span>
                                    {player.gamerTag && (
                                        <span className="text-secondary-500 text-sm ml-2">
                                            ({player.playerName})
                                        </span>
                                    )}
                                </div>
                                <div className="text-sm text-secondary-400">
                                    ⚔️ {player.stats.challengesWon}/{player.stats.challengesIssued} •
                                    🛡️ {player.stats.defensesWon}/{player.stats.defensesPlayed}
                                </div>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); movePlayerUp(player.id); }}
                                        disabled={saving || player.rank === 1}
                                        className="p-1.5 rounded hover:bg-secondary-600 disabled:opacity-30 disabled:cursor-not-allowed text-secondary-400 hover:text-green-400 transition-colors"
                                        title="Move up"
                                    >
                                        ↑
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); movePlayerDown(player.id); }}
                                        disabled={saving || player.rank === rankings.length}
                                        className="p-1.5 rounded hover:bg-secondary-600 disabled:opacity-30 disabled:cursor-not-allowed text-secondary-400 hover:text-red-400 transition-colors"
                                        title="Move down"
                                    >
                                        ↓
                                    </button>
                                </div>
                                <button
                                    onClick={() => removePlayerFromLadder(player.id)}
                                    className="text-red-400 hover:text-red-300 text-sm"
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Add Player Modal */}
                    {showAddPlayer && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                            <div className="card max-w-md w-full mx-4 max-h-[80vh] overflow-auto">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-bold text-white">Add Player to Ladder</h3>
                                    <button
                                        onClick={() => setShowAddPlayer(false)}
                                        className="text-secondary-400 hover:text-white"
                                    >
                                        ✕
                                    </button>
                                </div>

                                {availablePlayers.length === 0 ? (
                                    <p className="text-secondary-400 text-center py-8">
                                        All Smash players are already on the ladder
                                    </p>
                                ) : (
                                    <div className="space-y-2">
                                        {availablePlayers.map(player => (
                                            <button
                                                key={player.id}
                                                onClick={() => addPlayerToLadder(player)}
                                                disabled={saving}
                                                className="w-full p-3 rounded-lg bg-secondary-700 hover:bg-secondary-600 text-left transition-colors"
                                            >
                                                <span className="font-medium text-white">
                                                    {player.gamerTag || `${player.firstName} ${player.lastName}`}
                                                </span>
                                                {player.gamerTag && (
                                                    <span className="text-secondary-400 text-sm ml-2">
                                                        ({player.firstName} {player.lastName})
                                                    </span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Challenges Tab */}
            {activeTab === 'challenges' && (
                <div className="space-y-4">
                    {challenges.length === 0 ? (
                        <div className="card text-center py-12">
                            <p className="text-secondary-400">No active challenges</p>
                        </div>
                    ) : (
                        challenges.map(challenge => (
                            <div key={challenge.id} className="card p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-4">
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${challenge.status === 'disputed'
                                            ? 'bg-red-500/20 text-red-400'
                                            : challenge.status === 'awaiting_confirm'
                                                ? 'bg-yellow-500/20 text-yellow-400'
                                                : 'bg-secondary-700 text-secondary-300'
                                            }`}>
                                            {challenge.status.toUpperCase().replace('_', ' ')}
                                        </span>
                                    </div>
                                    <span className="text-secondary-500 text-sm">
                                        {challenge.createdAt?.toDate?.().toLocaleDateString() || 'Unknown'}
                                    </span>
                                </div>

                                <div className="flex items-center gap-4 mb-4">
                                    <div className="flex-1 text-center">
                                        <div className="text-sm text-secondary-400">Challenger</div>
                                        <div className="font-bold text-white">{challenge.challengerName}</div>
                                        <div className="text-secondary-500 text-sm">Rank #{challenge.challengerRankAtTime}</div>
                                    </div>
                                    <div className="text-2xl text-secondary-500">⚔️</div>
                                    <div className="flex-1 text-center">
                                        <div className="text-sm text-secondary-400">Defender</div>
                                        <div className="font-bold text-white">{challenge.defenderName}</div>
                                        <div className="text-secondary-500 text-sm">Rank #{challenge.defenderRankAtTime}</div>
                                    </div>
                                </div>

                                {/* Reports (if preseason) */}
                                {(challenge.challengerReport || challenge.defenderReport) && (
                                    <div className="mb-4 p-3 bg-secondary-800 rounded-lg text-sm">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <span className="text-secondary-400">Challenger says: </span>
                                                <span className="text-white">
                                                    {challenge.challengerReport?.claimedWinner === 'challenger' ? 'I won' : 'I lost'}
                                                    {challenge.challengerReport?.score && ` (${challenge.challengerReport.score})`}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-secondary-400">Defender says: </span>
                                                <span className="text-white">
                                                    {challenge.defenderReport?.claimedWinner === 'defender' ? 'I won' : 'I lost'}
                                                    {challenge.defenderReport?.score && ` (${challenge.defenderReport.score})`}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="flex gap-2 justify-end">
                                    <button
                                        onClick={() => cancelChallenge(challenge.id)}
                                        disabled={saving}
                                        className="px-3 py-1.5 bg-secondary-700 text-secondary-300 rounded hover:bg-secondary-600 text-sm"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => resolveChallenge(challenge.id, 'defender')}
                                        disabled={saving}
                                        className="px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 text-sm"
                                    >
                                        🛡️ Defender Wins
                                    </button>
                                    <button
                                        onClick={() => resolveChallenge(challenge.id, 'challenger')}
                                        disabled={saving}
                                        className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 text-sm"
                                    >
                                        ⚔️ Challenger Wins
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
                <div className="card space-y-6">
                    <h3 className="text-lg font-bold text-white">Ladder Settings</h3>

                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Mode */}
                        <div>
                            <label className="block text-sm font-medium text-secondary-300 mb-2">
                                Season Mode
                            </label>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setSettings(s => ({ ...s, isPreseason: true }))}
                                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${settings.isPreseason
                                        ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                                        : 'bg-secondary-700 text-secondary-400'
                                        }`}
                                >
                                    ⚡ Preseason
                                </button>
                                <button
                                    onClick={() => setSettings(s => ({ ...s, isPreseason: false }))}
                                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${!settings.isPreseason
                                        ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                                        : 'bg-secondary-700 text-secondary-400'
                                        }`}
                                >
                                    🏆 Regular Season
                                </button>
                            </div>
                            <p className="text-secondary-500 text-xs mt-1">
                                {settings.isPreseason
                                    ? 'Players can self-report match results'
                                    : 'Matches must be verified by admin'}
                            </p>
                        </div>

                        {/* Challenge Range */}
                        <div>
                            <label className="block text-sm font-medium text-secondary-300 mb-2">
                                Default Challenge Range
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="10"
                                value={settings.defaultChallengeRange}
                                onChange={e => setSettings(s => ({ ...s, defaultChallengeRange: parseInt(e.target.value) || 3 }))}
                                className="input w-full"
                            />
                            <p className="text-secondary-500 text-xs mt-1">
                                How many ranks ahead players can challenge
                            </p>
                        </div>

                        {/* Top Tier Threshold */}
                        <div>
                            <label className="block text-sm font-medium text-secondary-300 mb-2">
                                Top Tier Threshold
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="20"
                                value={settings.topTierThreshold}
                                onChange={e => setSettings(s => ({ ...s, topTierThreshold: parseInt(e.target.value) || 5 }))}
                                className="input w-full"
                            />
                            <p className="text-secondary-500 text-xs mt-1">
                                Ranks 1-{settings.topTierThreshold} have stricter challenge rules
                            </p>
                        </div>

                        {/* Top Tier Range */}
                        <div>
                            <label className="block text-sm font-medium text-secondary-300 mb-2">
                                Top Tier Challenge Range
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="5"
                                value={settings.topTierChallengeRange}
                                onChange={e => setSettings(s => ({ ...s, topTierChallengeRange: parseInt(e.target.value) || 1 }))}
                                className="input w-full"
                            />
                            <p className="text-secondary-500 text-xs mt-1">
                                How many ranks ahead top tier can challenge
                            </p>
                        </div>

                        {/* Default Format */}
                        <div>
                            <label className="block text-sm font-medium text-secondary-300 mb-2">
                                Default Match Format
                            </label>
                            <select
                                value={settings.defaultFormat}
                                onChange={e => setSettings(s => ({ ...s, defaultFormat: e.target.value }))}
                                className="input w-full"
                            >
                                <option value="Bo1">Best of 1</option>
                                <option value="Bo3">Best of 3</option>
                                <option value="Bo5">Best of 5</option>
                            </select>
                        </div>

                        {/* Notifications */}
                        <div className="space-y-3">
                            <label className="block text-sm font-medium text-secondary-300">
                                Discord Notifications
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={settings.notifyOnChallenge}
                                    onChange={e => setSettings(s => ({ ...s, notifyOnChallenge: e.target.checked }))}
                                    className="w-4 h-4 rounded border-secondary-500"
                                />
                                <span className="text-secondary-300">Notify on new challenges</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={settings.notifyOnResult}
                                    onChange={e => setSettings(s => ({ ...s, notifyOnResult: e.target.checked }))}
                                    className="w-4 h-4 rounded border-secondary-500"
                                />
                                <span className="text-secondary-300">Announce results</span>
                            </label>
                        </div>
                    </div>

                    <div className="pt-4 border-t border-secondary-700">
                        <button
                            onClick={saveSettings}
                            disabled={saving}
                            className="btn-primary"
                        >
                            {saving ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </div>
            )}

            {/* Saving Indicator */}
            {saving && (
                <div className="fixed bottom-4 right-4 bg-secondary-800 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                </div>
            )}
        </div>
    );
}
